import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { userService } from '../services/users/userService';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('THEME_CONTEXT');

export type ThemeMode = 'light' | 'dark' | 'auto' | 'openai-dark';
export type EffectiveTheme = 'light' | 'dark' | 'openai-dark';

export type ThemeScheme = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemeMode;
  effectiveTheme: EffectiveTheme;
  themeScheme: ThemeScheme;
  setTheme: (theme: ThemeMode) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Reads the runtime-resolved value of --c-bg by applying it to a hidden probe
 * element and reading the browser-computed background-color. This stays in sync
 * with the SCSS token chain automatically without hardcoding hex values here.
 */
function resolveThemeColorFromCss(): string {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;left:-9999px;background-color:var(--c-bg)';
  document.documentElement.appendChild(probe);
  const resolved = getComputedStyle(probe).backgroundColor;
  probe.remove();
  return resolved || '#ffffff';
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const cached = localStorage.getItem('theme') as ThemeMode | null;
      if (cached && ['light', 'dark', 'auto', 'openai-dark'].includes(cached)) {
        return cached;
      }
    } catch (e) {}
    return 'light';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // Detect system color scheme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPrefersDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
      log.log('[ThemeContext] System preference changed:', e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Calculate effective theme (resolve 'auto' to 'light' or 'dark')
  const effectiveTheme: EffectiveTheme = 
    theme === 'auto' 
      ? (systemPrefersDark ? 'dark' : 'light')
      : (theme as EffectiveTheme);

  // Determine theme scheme (light or dark) - useful for components
  const themeScheme: ThemeScheme = effectiveTheme === 'light' ? 'light' : 'dark';

  // Apply theme to document
  useEffect(() => {
    const htmlElement = document.documentElement;
    
    // Add transitioning class to prevent flash
    htmlElement.classList.add('theme-transitioning');
    
    // Set data-theme attribute
    htmlElement.setAttribute('data-theme', theme);
    
    // Add theme scheme classes for extensible dark/light targeting
    htmlElement.classList.remove('theme-scheme-light', 'theme-scheme-dark');
    htmlElement.classList.add(`theme-scheme-${themeScheme}`);

    // Keep the browser-chrome tint (mobile Safari/Chrome UI + overscroll/safe
    // areas) in sync with the active theme by reading --c-bg at runtime.
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', resolveThemeColorFromCss());
    }
    
    // Remove transitioning class after a brief delay
    const timeout = setTimeout(() => {
      htmlElement.classList.remove('theme-transitioning');
    }, 50);
    
    log.log('[ThemeContext] Applied theme:', theme, '(effective:', effectiveTheme, 'scheme:', themeScheme, ')');
    
    return () => clearTimeout(timeout);
  }, [theme, effectiveTheme, themeScheme]);

  // Sync theme from backend after initial render — localStorage already applied synchronously,
  // so the user sees the correct theme immediately. The backend sync only matters if the
  // theme changed on another device. Defer it so it doesn't race with the initial auth flow.
  useEffect(() => {
    // Theme is already correct from localStorage — nothing is actively "loading".
    setIsLoading(false);

    let cancelled = false;

    const syncThemeFromBackend = async () => {
      if (cancelled) return;
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      try {
        const profile = await userService.getUserProfile();
        if (cancelled) return;
        const backendTheme = profile?.skeleton?.preferences?.theme as ThemeMode | undefined;

        if (backendTheme && ['light', 'dark', 'auto', 'openai-dark'].includes(backendTheme)) {
          const currentCached = localStorage.getItem('theme');
          if (backendTheme !== currentCached) {
            setThemeState(backendTheme);
            localStorage.setItem('theme', backendTheme);
          }
        }
      } catch (error) {
        log.warn('[ThemeContext] Failed to sync theme from backend:', error);
      }
    };

    // Defer the initial mount sync so it doesn't compete with the auth flow on mount.
    // Only worth scheduling if a token already exists — otherwise there's nothing to sync yet.
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    if (localStorage.getItem('authToken')) {
      if ('requestIdleCallback' in window) {
        idleId = (window as any).requestIdleCallback(syncThemeFromBackend, { timeout: 5000 });
      } else {
        timerId = setTimeout(syncThemeFromBackend, 2000);
      }
    }

    // Re-sync immediately when auth completes mid-session (e.g. signing up via a
    // modal on a public marketing page, where authToken didn't exist yet at mount
    // so the deferred sync above never scheduled). Without this listener, a
    // freshly-persisted backend preference (e.g. dark mode for the NSFW funnel)
    // only takes effect after a full page reload re-mounts this provider with
    // the token already present. `auth-refresh` is dispatched by useAuthForm
    // right after tokens are written to localStorage.
    const handleAuthRefresh = () => {
      syncThemeFromBackend();
    };
    window.addEventListener('auth-refresh', handleAuthRefresh);

    return () => {
      cancelled = true;
      if (idleId !== undefined) (window as any).cancelIdleCallback(idleId);
      if (timerId !== undefined) clearTimeout(timerId);
      window.removeEventListener('auth-refresh', handleAuthRefresh);
    };
  }, []);

  // Listen for external settings updates (e.g. from the settings_update AI tool via tool-runtime).
  // The backend has already been updated by tool-runtime, so we only update local state.
  useEffect(() => {
    const handleSettingsApplied = (e: Event) => {
      const { setting, value } = (e as CustomEvent).detail ?? {};
      if (setting === 'theme' && value && ['light', 'dark', 'auto', 'openai-dark'].includes(value)) {
        log.log('[ThemeContext] river:settings-applied — applying theme:', value);
        setThemeState(value as ThemeMode);
        try { localStorage.setItem('theme', value); } catch {}
      }
    };
    window.addEventListener('river:settings-applied', handleSettingsApplied);
    return () => window.removeEventListener('river:settings-applied', handleSettingsApplied);
  }, []);

  // Set theme and persist to backend
  const setTheme = useCallback(async (newTheme: ThemeMode) => {
    try {
      log.log('[ThemeContext] Setting theme:', newTheme);
      
      // Update state immediately for UI responsiveness
      setThemeState(newTheme);
      localStorage.setItem('theme', newTheme);
      
      // Persist to backend asynchronously
      try {
        await userService.updateUserPreferences({
          theme: newTheme
        });
        log.log('[ThemeContext] Theme persisted to backend');
      } catch (error) {
        log.error('[ThemeContext] Failed to persist theme to backend:', error);
        // Don't revert - local state is still valid
      }
    } catch (error) {
      log.error('[ThemeContext] Error setting theme:', error);
      throw error;
    }
  }, []);

  const value: ThemeContextType = {
    theme,
    effectiveTheme,
    themeScheme,
    setTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

